import type { OrderSide } from '@/analytics';

/**
 * Pure helpers for the binary-markets (ARC-82) surfaces.
 *
 * Pricing convention (read-API seed + Centrifugo live):
 * - Seed prices arrive from the API as stringified probabilities in `[0, 1]`
 *   (e.g. `"0.23"`).
 * - Live `PriceUpdated` events arrive with `yesPrice` / `noPrice` already in
 *   **cents (1–99)**.
 *
 * Everything downstream works in **cents** (0–100), so {@link toCents}
 * normalises both sources to the same scale before any display math.
 */

/** A live price overlay for a single market, kept in cents (0–100). */
export interface LivePrice {
  /** Yes-side price in cents (0–100). */
  yesCents: number;
  /** No-side price in cents (0–100). */
  noCents: number;
}

const volumeFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/**
 * Format a raw volume string (e.g. `"1469300.0000"`) as a grouped integer
 * for the `$ … Vol` footer / header rows. Falls back to `0` on bad input.
 */
export function formatVolume(raw: string | null | undefined): string {
  const n = parseFloat(raw ?? '');
  return Number.isFinite(n) ? volumeFormatter.format(n) : '0';
}

/**
 * Normalise a dollar-probability price in `[0, 1]` — the API's stringified
 * seeds and Plaee's numeric wire/snapshot values alike — to whole-number
 * cents (0–100). Returns `null` when the value is missing / unparseable.
 */
export function toCents(price: string | number | null | undefined): number | null {
  if (price == null) return null;
  const n = typeof price === 'number' ? price : parseFloat(price);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/**
 * Whole-number percentage (0–100) for a side, derived from its cents price.
 * `null` cents → `null`.
 */
export function centsToPercent(cents: number | null): number | null {
  return cents == null ? null : Math.round(cents);
}

/**
 * Payout multiplier for a side from a cents price — `≈ 100 ÷ cents`, the
 * dollar return on a winning $1 contract. `null`/0 cents → `null`.
 *
 * Prefer the server-provided `yesPayout` / `noPayout` when present; use this
 * only to recompute a live payout from a moved price.
 */
export function centsToPayout(cents: number | null): number | null {
  if (cents == null || cents <= 0) return null;
  return 100 / cents;
}

/** Format a payout multiplier as e.g. `4.13x`. `null` → `—`. */
export function formatPayout(multiplier: number | null | undefined): string {
  return multiplier == null ? '—' : `${multiplier.toFixed(2)}x`;
}

/** Format a percentage with its outcome side, e.g. `23% Yes` / `77% No`. */
export function formatPercentForSide(percent: number | null, side: OrderSide): string {
  const label = side === 'yes' ? 'Yes' : 'No';
  return percent == null ? `— ${label}` : `${percent}% ${label}`;
}

/** Outcome / market lifecycle statuses that mean it has resolved and is no longer tradeable. */
const TERMINAL_STATUSES = new Set(['SETTLED', 'CLOSED', 'CANCELLED']);

/**
 * Whether an outcome is still live (tradeable) and should be shown on cards /
 * detail rows.
 *
 * Settlement stamps a `result` (and a terminal `status`) on a resolved outcome
 * but does NOT flip `isActive`, so a resolved outcome inside a still-ACTIVE
 * multi-outcome market would otherwise leak through an `isActive`-only filter.
 * Hide it when it carries a result or a terminal status; still require `isActive`.
 */
export function isLiveOutcome(o: { isActive: boolean; result: string | null; status: string | null }): boolean {
  if (!o.isActive) return false;
  if (o.result != null && o.result !== '') return false;
  return !TERMINAL_STATUSES.has((o.status ?? '').toUpperCase());
}

/** Whether a market is live (tradeable) — terminal `status` markets are hidden. */
export function isLiveMarketStatus(status: string | null | undefined): boolean {
  return !TERMINAL_STATUSES.has((status ?? '').toUpperCase());
}
